import { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave } from 'react-icons/fa';
import api from '../../api';
import { useColumnVisibility, ColumnPicker } from '../../components/ColumnPicker';

const empty = { name: '', date: '', description: '' };
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

export default function AdminHolidays() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [year, setYear]         = useState(CURRENT_YEAR);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]         = useState(empty);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.getHolidays(year)
      .then(setHolidays)
      .catch(() => setError('Failed to load holidays.'))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(load, [load]);

  const openNew  = () => { setForm({ ...empty, date: `${year}-01-01` }); setEditingId(null); setShowForm(true); setError(''); };
  const openEdit = (h) => {
    setForm({ name: h.name, date: h.date.split('T')[0], description: h.description || '' });
    setEditingId(h.id);
    setShowForm(true);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await api.updateHoliday(editingId, form);
        setSuccess('Holiday updated.');
      } else {
        await api.createHoliday(form);
        setSuccess('Holiday added.');
      }
      setShowForm(false);
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`Delete "${h.name}"?`)) return;
    try {
      await api.deleteHoliday(h.id);
      setSuccess('Holiday deleted.');
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete.');
    }
  };

  const fmt = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

  const HOL_COLS = [{ key: 'description', label: 'Description' }];
  const { visible: holCols, toggle: toggleHolCol, show: showHolCol } = useColumnVisibility(HOL_COLS, 'cols_holidays');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-10">

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#0c1b4d]">Company Holidays</h1>
            <p className="text-gray-500 text-sm mt-1">Manage official holidays — they auto-fill in employee timesheets.</p>
          </div>
          <button onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors">
            <FaPlus size={12} /> Add Holiday
          </button>
        </div>

        {error   && <p className="text-red-600 text-sm mb-4 bg-red-50 px-4 py-2 rounded-lg">{error}</p>}
        {success && <p className="text-green-700 text-sm mb-4 bg-green-50 px-4 py-2 rounded-lg">{success}</p>}

        {/* Year selector */}
        <div className="flex gap-2 mb-6">
          {YEARS.map(y => (
            <button key={y} onClick={() => setYear(y)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${year === y ? 'bg-[#0c1b4d] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-slate-50'}`}>
              {y}
            </button>
          ))}
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-bold text-[#0c1b4d]">{editingId ? 'Edit Holiday' : 'Add Holiday'}</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
              </div>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Holiday Name *</label>
                  <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="e.g. Independence Day"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Date *</label>
                  <input required type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Description <span className="font-normal text-gray-400">(optional)</span></label>
                  <input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                    placeholder="e.g. Office closed"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                {error && <p className="text-red-600 text-xs">{error}</p>}
                <div className="flex gap-3 pt-2">
                  <button type="submit" disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors disabled:opacity-60">
                    <FaSave size={12} /> {saving ? 'Saving...' : 'Save Holiday'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-slate-50">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : holidays.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <p className="text-gray-400 text-sm mb-3">No holidays for {year}.</p>
            <button onClick={openNew} className="px-4 py-2 bg-[#0c1b4d] text-white text-sm rounded-xl hover:bg-blue-900">
              Add First Holiday
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100">
            <ColumnPicker columns={HOL_COLS} visible={holCols} onToggle={toggleHolCol} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Holiday</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                    {showHolCol('description') && <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Description</th>}
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {holidays.map(h => (
                    <tr key={h.id} className="border-b border-gray-50 hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 font-medium text-[#0c1b4d] whitespace-nowrap">{h.name}</td>
                      <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{fmt(h.date)}</td>
                      {showHolCol('description') && <td className="px-5 py-3 text-gray-400">{h.description || '—'}</td>}
                      <td className="px-5 py-3">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => openEdit(h)} className="p-2 text-gray-400 hover:text-[#0c1b4d] hover:bg-blue-50 rounded-lg transition-colors"><FaEdit size={13} /></button>
                          <button onClick={() => handleDelete(h)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><FaTrash size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
